import {
  curry,
  isNil,
  modulo,
  equals,
  __,
  compose,
  range,
  filter,
  T,
} from 'ramda'
import { isInput, set, isOutput } from 'utils/types'
import strings from 'utils/strings'

import {
  TWO_BIT_OUTPUT_MAX_INDEX_AVAILABLE,
  MAX_BITS,
  XMC1,
  XMC2,
  XMC3,
  XMC4,
  XMC5,
  XMC6,
} from 'utils/constants'

const BYTE_SIZE = 8
const isEven = compose(equals(0), modulo(__, 2))
const selectableBits = compose(set, filter(isEven), range(0))
const validXMCModuleRanges = [XMC1, XMC2, XMC3, XMC4, XMC5, XMC6]

export const getTheRange = curry((groupSize, index) => {
  if (isNil(index)) return {}

  return {
    startBit: index,
    endBit: index + groupSize - 1,
  }
})

export const isInRange = curry((bitRange, index) => {
  const { startBit, endBit } = bitRange

  const inRange = index >= startBit && index <= endBit
  const inValidRange = endBit < MAX_BITS

  return inRange && inValidRange
})

// https://math.stackexchange.com/questions/1611927/how-to-compute-for-the-number-of-sequential-combinations-possible-in-a-set
const sequentialCombinations = (sampleSize, groupSize) =>
  sampleSize - (groupSize - 1)

export const isAvailableForInput = (type, groupSize, featureModuleName) => {
  if (groupSize === 1) return T

  // 16 bit selections do not have any sequential combinations
  // so force that to 1
  const isModuleWithinXMCRange =
    validXMCModuleRanges.includes(featureModuleName)

  const numberOfSelectionsPerRow =
    groupSize === 16 ? 1 : sequentialCombinations(BYTE_SIZE, groupSize)

  const validSelections = selectableBits(numberOfSelectionsPerRow)
  const maxBit =
    isOutput(type) && groupSize === 2
      ? TWO_BIT_OUTPUT_MAX_INDEX_AVAILABLE
      : MAX_BITS

  return (index) => {
    const row = Math.floor(index / BYTE_SIZE)
    const inRange = index + groupSize <= maxBit
    const isValidSelectionBit = validSelections.has(bitValue(index))

    if (
      row === 4 &&
      isModuleWithinXMCRange &&
      bitValue(index) <= 3 &&
      isValidSelectionBit
    ) {
      return true
    }

    // for 16 bit outputs, only allow selection on even bytes
    const isValidSelectionByte =
      groupSize === 16 && isOutput(type) ? isEven(row) : true

    return inRange && isValidSelectionBit && isValidSelectionByte
  }
}

export const calculateNewStartBit = (
  currentStartBit,
  groupSize,
  type,
  disabledBits = []
) => {
  const { startBit, endBit } = getTheRange(groupSize, currentStartBit)
  const inSelectedRange = isInRange({ startBit, endBit })
  const isAvailable = isAvailableForInput(type, groupSize)

  const willReset =
    disabledBits.some(inSelectedRange) || !isAvailable(currentStartBit)

  return willReset ? null : currentStartBit
}

export const gridLabel = (groupSize, type, value) => {
  if (!isNil(value)) {
    return isInput(type)
      ? strings.bit_byte_input_label(groupSize)
      : strings.bit_byte_output_label(groupSize)
  }

  return isInput(type)
    ? strings.select_starting_bit_label
    : strings.select_bit_byte_output_label(groupSize)
}

export const bitValue = (index) => index % BYTE_SIZE

const byteOfBit = (bit) => Math.floor(bit / BYTE_SIZE)

export const bitBytePairLabel = (startBit, endBit) => {
  if (isNil(startBit)) return strings.unassigned_condition

  const startBitValue = bitValue(startBit)
  const endBitValue = bitValue(endBit)
  const startByte = byteOfBit(startBit)
  const endByte = byteOfBit(endBit)

  const singleBitLabel = `${strings.bit} ${startBit}`
  const sameByteBlockLabel = `${strings.byte} ${startByte}, ${strings.bits} ${startBitValue} - ${endBitValue}`
  const multiByteBlockLabel = `${strings.byte} ${startByte},
      ${strings.bit} ${startBitValue} - ${strings.byte} ${endByte},
      ${strings.bit} ${endBitValue}`

  if (startBit === endBit) return singleBitLabel
  return startByte === endByte ? sameByteBlockLabel : multiByteBlockLabel
}
